// src/pages/lokasi/utils/importExcelHandler.js
import * as XLSX from "xlsx";
import { ref, push, update } from "firebase/database";
import { database } from "../../../firebase";

// Helper function to convert Excel date number to DD-MM-YYYY string
export const excelDateToDDMMYYYY = (excelDate) => {
    if (typeof excelDate === 'number' && excelDate > 0) {
        const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }
    return excelDate;
};

// Helper: ubah onValue menjadi Promise
export const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        const { onValue } = require("firebase/database");
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

/**
 * Fungsi utama untuk mengimpor data dari Excel ke Firebase
 * @param {File} file - File Excel yang akan diimpor
 * @param {Object} callbacks - Object berisi callback functions
 * @returns {Promise<Object>} - Object berisi hasil import (importedCount, updatedCount, ignoredCount)
 */
export const handleImportExcel = async (file, callbacks = {}) => {
    const {
        onStart = () => {},
        onProgress = () => {},
        onSuccess = () => {},
        onError = () => {},
        onComplete = () => {},
        updateDataList = () => {},
        updateLastUpdated = () => {}
    } = callbacks;

    if (!file) {
        throw new Error("Tidak ada file yang dipilih.");
    }

    try {
        onStart("Memulai proses import...");

        // Baca file Excel
        const data = await readExcelFile(file);
        const jsonData = XLSX.utils.sheet_to_json(data.worksheet);

        // Dapatkan referensi Firebase
        const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
        const existingLokasiSnapshot = await onValuePromise(lokasiRef);
        const existingLokasiData = existingLokasiSnapshot.val() || {};

        // Buat mapping data yang sudah ada
        const existingLokasiMap = new Map();
        Object.keys(existingLokasiData).forEach(fbKey => {
            const record = existingLokasiData[fbKey];
            existingLokasiMap.set(record.id_lokasi, fbKey);
        });

        // Kumpulkan ID lokasi dari Excel untuk pengecekan
        const excelIdLokasiSet = new Set();
        jsonData.forEach(item => {
            const lokasi = item["Lokasi"];
            const mulaiRawatExcel = item["Tanggal mulai rawat"];
            if (lokasi && mulaiRawatExcel) {
                excelIdLokasiSet.add(`${lokasi}${mulaiRawatExcel}`);
            }
        });

        const importTimestamp = new Date().toISOString().split('T')[0];

        // Tandai data yang tidak ada di Excel sebagai 'closing'
        await markClosingRecords(existingLokasiData, excelIdLokasiSet, importTimestamp);

        // Proses import data dari Excel
        const results = await processExcelData(
            jsonData,
            existingLokasiMap,
            lokasiRef,
            importTimestamp
        );

        // Update data terakhir
        updateLastUpdated(new Date());

        // Ambil data terbaru dari Firebase
        const updatedLokasiSnapshot = await onValuePromise(lokasiRef);
        const updatedLokasiData = updatedLokasiSnapshot.val() || {};
        const updatedLoadedLokasi = Object.keys(updatedLokasiData).map(key => ({
            firebaseKey: key,
            ...updatedLokasiData[key]
        }));

        // Filter hanya data aktif
        const activeData = updatedLoadedLokasi.filter(item => item.status_plantation !== 'closing');
        updateDataList(activeData);

        onSuccess({
            importedCount: results.importedCount,
            updatedCount: results.updatedCount,
            ignoredCount: results.ignoredCount,
            totalProcessed: results.importedCount + results.updatedCount
        });

        return results;

    } catch (error) {
        console.error("Gagal impor Excel:", error);
        onError(error);
        throw error;
    } finally {
        onComplete();
    }
};

/**
 * Membaca file Excel dan mengembalikan worksheet
 * @param {File} file - File Excel
 * @returns {Promise<Object>} - Object berisi workbook dan worksheet
 */
const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                resolve({
                    workbook,
                    worksheet,
                    sheetName
                });
            } catch (error) {
                reject(error);
            }
        };
        
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Menandai record yang tidak ada di Excel sebagai 'closing'
 */
const markClosingRecords = async (existingData, excelIdSet, importTimestamp) => {
    const updatePromises = [];
    
    for (const fbKey in existingData) {
        const record = existingData[fbKey];
        if (!excelIdSet.has(record.id_lokasi)) {
            const updatePromise = update(ref(database, `db_freshpine/tb_lokasi/${fbKey}`), {
                status_plantation: 'closing',
                last_imported_at: importTimestamp
            });
            updatePromises.push(updatePromise);
        }
    }
    
    await Promise.all(updatePromises);
};

/**
 * Memproses data dari Excel dan menyimpan ke Firebase
 */
const processExcelData = async (jsonData, existingLokasiMap, lokasiRef, importTimestamp) => {
    let importedCount = 0;
    let updatedCount = 0;
    let ignoredCount = 0;

    const processPromises = jsonData.map(async (item) => {
        const lokasi = item["Lokasi"];
        const mulaiRawatExcel = item["Tanggal mulai rawat"];
        const mulaiRawatFormatted = excelDateToDDMMYYYY(mulaiRawatExcel);

        if (!lokasi || !mulaiRawatExcel) {
            ignoredCount++;
            console.warn("Ignoring row due to missing Lokasi or Tanggal mulai rawat:", item);
            return;
        }

        const idLokasi = `${lokasi}${mulaiRawatExcel}`;

        // Siapkan data untuk disimpan
        const dataToSave = prepareDataForSave(item, idLokasi, mulaiRawatFormatted, importTimestamp);

        const firebaseKey = existingLokasiMap.get(idLokasi);

        if (firebaseKey) {
            await update(ref(database, `db_freshpine/tb_lokasi/${firebaseKey}`), dataToSave);
            updatedCount++;
        } else {
            await push(lokasiRef, dataToSave);
            importedCount++;
        }
    });

    await Promise.all(processPromises);

    return {
        importedCount,
        updatedCount,
        ignoredCount
    };
};

/**
 * Menyiapkan data untuk disimpan ke Firebase
 */
const prepareDataForSave = (item, idLokasi, mulaiRawatFormatted, importTimestamp) => {
    // Konversi nilai numerik ke string
    const brutoValue = item["Luas bruto"];
    const aktifValue = item["Luas aktif"];
    const nettoValue = item["Luas netto"];
    
    return {
        id_lokasi: idLokasi,
        lokasi: item["Lokasi"] || '',
        wilayah: item["Struc Plant"] || '',
        bruto: brutoValue !== undefined && brutoValue !== null ? String(brutoValue) : '',
        aktif: aktifValue !== undefined && aktifValue !== null ? String(aktifValue) : '',
        netto: nettoValue !== undefined && nettoValue !== null ? String(nettoValue) : '',
        status: item["Status"] || '',
        perubahan_status: excelDateToDDMMYYYY(item["Tanggal perubahan status"]) || '',
        mulai_rawat: mulaiRawatFormatted,
        mulai_tanam: excelDateToDDMMYYYY(item["Tanggal mulai tanam"]) || '',
        bibit: item["Bibit"] || '',
        deskripsi: item["Deskripsi"] || '',
        desc_lvl_01: item["Desc.Lvl 01"] || '',
        desc_lvl_02: item["Desc.Lvl 02"] || '',
        desc_lvl_03: item["Desc.Lvl 03"] || '',
        status_plantation: 'active',
        last_imported_at: importTimestamp
    };
};

/**
 * Validasi struktur file Excel
 * @param {File} file - File Excel
 * @returns {Promise<boolean>} - True jika valid
 */
export const validateExcelFile = async (file) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
        throw new Error("File harus berupa Excel (.xlsx atau .xls)");
    }

    const data = await readExcelFile(file);
    const jsonData = XLSX.utils.sheet_to_json(data.worksheet);
    
    if (jsonData.length === 0) {
        throw new Error("File Excel kosong atau tidak memiliki data");
    }

    // Cek kolom wajib
    const firstRow = jsonData[0];
    const requiredColumns = ["Lokasi", "Tanggal mulai rawat"];
    const missingColumns = requiredColumns.filter(col => !(col in firstRow));
    
    if (missingColumns.length > 0) {
        throw new Error(`Kolom wajib tidak ditemukan: ${missingColumns.join(", ")}`);
    }

    return true;
};